// src/pages/VendorPlatform.jsx

import { useEffect, useState, useRef } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { vendorArtistApi, getAuthToken, clearAuthSession, getImageUrl } from "../lib/api";
import { emptyDashboardData } from "../utils/vendor/vendorUtils";
import logo from "../assets/wamglammz-logo.png";
import logo2 from "../assets/wamglammz-logo2.png";

// Import layout components
import { WorkspaceHeader, WorkspaceSidebar } from "../components/vendor/vendorLayout";

// Import panel groups
import {
  DashboardOverview, ProfilePanel, SubscriptionPanel,
  NotificationsPanel, BookingLinkPanel
} from "../components/vendor/vendorPanels";
import {
  CalendarPanel, ClientsPanel
} from "../components/vendor/calendarBooking";
import {
  ServicesPanel
} from "../components/vendor/entityPanels";

// ==========================================
// Sub-component: Profile Completion Form
// ==========================================
function ProfileForm({ initialData = {}, onSubmitSuccess, onLogout }) {
  const [fullName, setFullName] = useState(initialData.fullname || initialData.name || "");
  const [businessName, setBusinessName] = useState(initialData.branding_settings?.businessName || initialData.business_name || "");
  const [email, setEmail] = useState(initialData.email || "");
  const [businessAddress, setBusinessAddress] = useState(initialData.businessAddress || initialData.business_address || "");
  const [bio, setBio] = useState(initialData.bio || "");
  const [profileImage, setProfileImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(initialData.profile_image ? getImageUrl(initialData.profile_image) : null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setProfileImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setIsSubmitting(true);

    try {
      const payload = {
        fullName,
        businessName,
        email,
        businessAddress,
        bio,
        role: "vendor-artist"
      };

      if (profileImage) {
        payload.profile_image = profileImage;
      }

      const res = await vendorArtistApi.saveProfile(payload);
      if (res.success) {
        onSubmitSuccess();
      } else {
        setError(res.message || "Failed to save profile.");
      }
    } catch (err) {
      setError(err.message || "An error occurred while saving the profile.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="mx-auto max-w-2xl px-4 py-8">
      <div className="rounded-2xl border border-blue-100 bg-white p-6 shadow-[0_16px_45px_rgba(14,63,182,0.07)] sm:p-8">
        <h2 className="text-2xl font-extrabold text-[#191E49]">Complete Your Vendor Profile</h2>
        <p className="mt-2 text-sm text-slate-500">
          Please provide your profile details to register as a vendor. Once submitted, our team will review and approve your application.
        </p>

        <form onSubmit={handleSubmit} className="mt-8 space-y-6">
          {error && (
            <div className="rounded-xl border border-red-100 bg-red-50 p-4 text-sm font-bold text-red-700">
              {error}
            </div>
          )}

          <div className="flex flex-col items-center gap-4 sm:flex-row">
            <div className="relative h-24 w-24 overflow-hidden rounded-full border-2 border-slate-200 bg-slate-50 shadow-inner">
              {imagePreview ? (
                <img src={imagePreview} alt="Preview" className="h-full w-full object-cover" />
              ) : (
                <div className="flex h-full w-full items-center justify-center text-slate-400">
                  <i className="fa-solid fa-user text-3xl"></i>
                </div>
              )}
            </div>
            <div>
              <label className="inline-flex cursor-pointer items-center rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-bold text-slate-700 shadow-sm transition hover:bg-slate-50">
                <span>Upload Profile Image</span>
                <input type="file" accept="image/*" onChange={handleImageChange} className="hidden" />
              </label>
              <p className="mt-1 text-xs text-slate-400">JPG, PNG, or GIF. Max 5MB.</p>
            </div>
          </div>

          <div className="grid gap-6 sm:grid-cols-2">
            <label className="block">
              <span className="text-sm font-bold text-slate-700">Business Name *</span>
              <input
                type="text"
                required
                value={businessName}
                onChange={(e) => setBusinessName(e.target.value)}
                placeholder="e.g. Glow Salon & Spa"
                className="mt-2 h-11 w-full rounded-xl border border-slate-200 px-4 text-sm font-semibold outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-100"
              />
            </label>

            <label className="block">
              <span className="text-sm font-bold text-slate-700">Full Name *</span>
              <input
                type="text"
                required
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                className="mt-2 h-11 w-full rounded-xl border border-slate-200 px-4 text-sm font-semibold outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-100"
              />
            </label>

            <label className="block sm:col-span-2">
              <span className="text-sm font-bold text-slate-700">Email Address *</span>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-2 h-11 w-full rounded-xl border border-slate-200 px-4 text-sm font-semibold outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-100"
              />
            </label>
          </div>

          <label className="block">
            <span className="text-sm font-bold text-slate-700">Business Address *</span>
            <input
              type="text"
              required
              value={businessAddress}
              onChange={(e) => setBusinessAddress(e.target.value)}
              placeholder="e.g. 123 Main St, City, State, Zip"
              className="mt-2 h-11 w-full rounded-xl border border-slate-200 px-4 text-sm font-semibold outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-100"
            />
          </label>

          <label className="block">
            <span className="text-sm font-bold text-slate-700">Bio</span>
            <textarea
              value={bio}
              onChange={(e) => setBio(e.target.value)}
              rows={4}
              placeholder="Tell us about your services and professional experience..."
              className="mt-2 w-full rounded-xl border border-slate-200 p-4 text-sm font-semibold outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-100"
            />
          </label>

          <div className="flex gap-4 pt-2">
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex h-12 flex-1 items-center justify-center rounded-xl bg-gradient-to-r from-[#0A3FB6] to-[#2FB4FF] text-sm font-bold text-white shadow-lg shadow-blue-100 hover:shadow-blue-200 transition disabled:opacity-75"
            >
              {isSubmitting ? "Saving Profile..." : "Submit Profile for Review"}
            </button>

            <button
              type="button"
              onClick={onLogout}
              className="h-12 rounded-xl border border-slate-200 bg-white px-6 text-sm font-bold text-slate-700 hover:bg-slate-50 transition"
            >
              Log Out
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ==========================================
// Sub-component: Pending Screen
// ==========================================
function PendingScreen({ profile, onRefresh, onLogout }) {
  return (
    <div className="mx-auto max-w-2xl px-4 py-8">
      <div className="rounded-2xl border border-blue-100 bg-white p-6 text-center shadow-[0_16px_45px_rgba(14,63,182,0.07)] sm:p-8">
        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-amber-50 text-amber-500 animate-pulse">
          <i className="fa-solid fa-clock-rotate-left text-3xl"></i>
        </div>
        <h2 className="mt-6 text-2xl font-extrabold text-[#191E49]">Verification Pending</h2>
        <span className="mt-2 inline-block rounded-full bg-amber-50 px-3 py-1 text-xs font-bold text-amber-700 uppercase tracking-wider">
          Under Review
        </span>
        <p className="mt-4 text-base leading-7 text-slate-600">
          Thank you for completing your profile! Our admin team is currently verifying your details. Your workspace will become active once approved.
        </p>

        <div className="mt-8 border-t border-slate-100 pt-6 text-left">
          <h3 className="text-sm font-bold uppercase tracking-wider text-slate-400">Submitted Details</h3>
          <dl className="mt-4 grid gap-4 sm:grid-cols-2 text-sm">
            {profile.profile_image && (
              <div className="sm:col-span-2 flex justify-start">
                <div className="h-16 w-16 overflow-hidden rounded-full border border-slate-200">
                  <img src={getImageUrl(profile.profile_image)} alt="Profile" className="h-full w-full object-cover" />
                </div>
              </div>
            )}
            <div>
              <dt className="font-bold text-slate-500">Full Name</dt>
              <dd className="mt-1 font-semibold text-slate-800">{profile.name}</dd>
            </div>
            <div>
              <dt className="font-bold text-slate-500">Email Address</dt>
              <dd className="mt-1 font-semibold text-slate-800">{profile.email}</dd>
            </div>
            <div>
              <dt className="font-bold text-slate-500">Phone Number</dt>
              <dd className="mt-1 font-semibold text-slate-800">{profile.phone}</dd>
            </div>
            {profile.business_address && (
              <div className="sm:col-span-2">
                <dt className="font-bold text-slate-500">Business Address</dt>
                <dd className="mt-1 font-semibold text-slate-800">{profile.business_address}</dd>
              </div>
            )}
          </dl>
          {profile.bio && (
            <div className="mt-4 border-t border-slate-50 pt-4 text-sm">
              <dt className="font-bold text-slate-500">Bio</dt>
              <dd className="mt-1 font-semibold text-slate-700 italic">"{profile.bio}"</dd>
            </div>
          )}
        </div>

        <div className="mt-8 flex gap-4">
          <button
            onClick={onRefresh}
            className="flex h-12 flex-1 items-center justify-center rounded-xl bg-gradient-to-r from-[#0A3FB6] to-[#2FB4FF] text-sm font-bold text-white shadow-lg shadow-blue-100 hover:shadow-blue-200 transition"
          >
            Refresh Status
          </button>
          <button
            onClick={onLogout}
            className="h-12 rounded-xl border border-slate-200 bg-white px-6 text-sm font-bold text-slate-700 hover:bg-slate-50 transition"
          >
            Log Out
          </button>
        </div>
      </div>
    </div>
  );
}

// ==========================================
// Sub-component: Rejected Screen
// ==========================================
function RejectedScreen({ profile, onSubmitSuccess, onLogout }) {
  return (
    <div className="mx-auto max-w-2xl px-4 py-8">
      <div className="mb-6 rounded-2xl border border-red-100 bg-red-50 p-6 text-center shadow-[0_16px_45px_rgba(14,63,182,0.07)] sm:p-8">
        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-red-100 text-red-500">
          <i className="fa-solid fa-circle-xmark text-3xl"></i>
        </div>
        <h2 className="mt-6 text-2xl font-extrabold text-[#191E49]">Application Rejected</h2>
        <span className="mt-2 inline-block rounded-full bg-red-100 px-3 py-1 text-xs font-bold text-red-700 uppercase tracking-wider">
          Rejected
        </span>
        <p className="mt-4 text-base leading-7 text-slate-600">
          We regret to inform you that your application was rejected. Please review and update your profile details below to resubmit.
        </p>
      </div>

      <ProfileForm initialData={profile} onSubmitSuccess={onSubmitSuccess} onLogout={onLogout} />
    </div>
  );
}

// ==========================================
// Sub-component: Suspended Screen
// ==========================================
function SuspendedScreen({ onLogout }) {
  return (
    <div className="mx-auto max-w-2xl px-4 py-8">
      <div className="rounded-2xl border border-red-100 bg-white p-6 text-center shadow-[0_16px_45px_rgba(14,63,182,0.07)] sm:p-8">
        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-red-50 text-red-500">
          <i className="fa-solid fa-ban text-3xl"></i>
        </div>
        <h2 className="mt-6 text-2xl font-extrabold text-[#191E49]">Account Suspended</h2>
        <span className="mt-2 inline-block rounded-full bg-red-50 px-3 py-1 text-xs font-bold text-red-700 uppercase tracking-wider">
          Suspended
        </span>
        <p className="mt-4 text-base leading-7 text-slate-600">
          Your vendor account has been suspended by the administrators due to a violation of our terms or policies. If you believe this was an error, please contact support.
        </p>
        <div className="mt-8">
          <button
            onClick={onLogout}
            className="h-12 w-full rounded-xl bg-gradient-to-r from-[#0A3FB6] to-[#2FB4FF] text-sm font-bold text-white shadow-lg shadow-blue-100 hover:shadow-blue-200 transition"
          >
            Log Out
          </button>
        </div>
      </div>
    </div>
  );
}

const getFriendlyErrorMessage = (errorMsg) => {
  if (!errorMsg) return "An unexpected error occurred. Please try again.";
  const lower = errorMsg.toLowerCase();
  if (lower.includes("failed to fetch") || lower.includes("networkerror") || lower.includes("inbound response pipeline")) {
    return "We are unable to reach our services. Please check your internet connection or try again shortly.";
  }
  if (lower.includes("unexpected token") || lower.includes("json") || lower.includes("parsing")) {
    return "The server returned an unreadable response. This might be a temporary issue; please try again shortly.";
  }
  return errorMsg;
};

// ==========================================
// Main Component
// ==========================================
export default function VendorPlatform() {
  const navigate = useNavigate();
  const location = useLocation();

  const [notifications, setNotifications] = useState(() => {
    try {
      const saved = localStorage.getItem("vendor_notifications");
      if (saved) {
        const parsed = JSON.parse(saved);
        const sixHoursAgo = Date.now() - 6 * 60 * 60 * 1000;
        return parsed
          .map(n => {
            if (typeof n === "object" && n !== null) {
              return n.timestamp ? n : { ...n, timestamp: Date.now() };
            }
            return { message: String(n), timestamp: Date.now() };
          })
          .filter(n => n.timestamp > sixHoursAgo);
      }
    } catch (e) {
      console.error("Error loading notifications from localStorage:", e);
    }
    return [];
  });

  const [hasNewNotification, setHasNewNotification] = useState(() => {
    try {
      const savedBadge = localStorage.getItem("vendor_has_new_notification");
      return savedBadge === "true";
    } catch (e) {
      return false;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem("vendor_notifications", JSON.stringify(notifications));
    } catch (e) {
      console.error("Error saving notifications to localStorage:", e);
    }
  }, [notifications]);

  useEffect(() => {
    try {
      localStorage.setItem("vendor_has_new_notification", String(hasNewNotification));
    } catch (e) {
      console.error("Error saving notification badge state to localStorage:", e);
    }
  }, [hasNewNotification]);

  const today = new Date();
  const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
  const [calendarDate, setCalendarDate] = useState(todayStr);
  const [calendarView, setCalendarView] = useState("monthly");
  const refreshRef = useRef(null);

  const clearNotifications = () => {
    setHasNewNotification(false);
    setNotifications([]);
  };

  const clearBadge = () => {
    setHasNewNotification(false);
  };

  const getTabFromUrl = () => {
    const params = new URLSearchParams(location.search);
    const tab = params.get("tab") || "overview";
    if (tab === "bookings" || tab === "booking") return "calendar";
    return tab;
  };

  const [activeTab, setActiveTab] = useState(getTabFromUrl);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [dashboardData, setDashboardData] = useState(emptyDashboardData);

  useEffect(() => {
    refreshRef.current = fetchProfileAndDashboard;
  });

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tab = params.get("tab") || "overview";
    if (tab === "bookings" || tab === "booking") {
      navigate("/for-business/dashboard?tab=calendar", { replace: true });
      return;
    }
    const currentTab = getTabFromUrl();
    if (activeTab !== currentTab) {
      setActiveTab(currentTab);
    }
  }, [location.search]);

  useEffect(() => {
    if (!dashboardData.profile?.contactId) return;

    const serverBase = process.env.REACT_APP_API_BASE_URL || "http://localhost:8000/api";
    const cleanServerBase = serverBase.replace(/\/api$/, "");
    const sseUrl = `${cleanServerBase}/api/realtime-sync?vendorId=${dashboardData.profile.contactId}`;
    console.log("[SSE Connection] Registering real-time listener:", sseUrl);

    const eventSource = new EventSource(sseUrl);

    eventSource.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        console.log("[SSE Event Received]:", data);
        if (data.type === "booking_confirmed") {
          try {
            const audio = new Audio("https://assets.mixkit.co/active_storage/sfx/2869/2869-600.wav");
            audio.volume = 0.5;
            audio.play().catch(() => { });
          } catch (soundErr) { }

          setNotifications(prev => [
            {
              id: data.bookingId,
              message: data.message,
              date: data.bookingDate,
              artistId: data.artistId,
              timestamp: Date.now(),
            },
            ...prev
          ]);
          setHasNewNotification(true);

          if (refreshRef.current) {
            refreshRef.current();
          }
        }
      } catch (err) {
        console.error("[SSE Parse Error]:", err);
      }
    };

    eventSource.onerror = (err) => {
      console.error("[SSE Listener Error]:", err);
    };

    return () => {
      eventSource.close();
    };
  }, [dashboardData.profile?.contactId]);

  const [profileLoaded, setProfileLoaded] = useState(false);
  const [isDashboardLoading, setIsDashboardLoading] = useState(true);
  const [dashboardError, setDashboardError] = useState("");

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    navigate(`/for-business/dashboard?tab=${tab}`, { replace: true });
  };

  const handleLogout = () => {
    clearAuthSession();
    navigate("/for-business/login", { replace: true });
  };

  // State Management Helpers
  const updateStateArray = (key, identifier, updatedItem) => {
    setDashboardData(cur => ({
      ...cur,
      [key]: cur[key].map(item => (item.id || item[`${key.slice(0, -1)}id`]) === identifier ? { ...item, ...updatedItem } : item)
    }));
  };

  const filterStateArray = (key, identifier) => {
    setDashboardData(cur => ({
      ...cur,
      [key]: cur[key].filter(item => (item.id || item[`${key.slice(0, -1)}id`]) !== identifier)
    }));
  };

  // Profile
  const saveProfileToState = (profile) => setDashboardData(cur => ({ ...cur, profile }));

  // Services State
  const addServiceToState = (s) => setDashboardData(cur => ({ ...cur, services: [s, ...cur.services] }));
  const updateServiceInState = (s) => updateStateArray('services', s.serviceid || s.id, s);
  const removeServiceFromState = (id) => filterStateArray('services', id);

  // Categories State
  const addCategoryToState = (c) => setDashboardData(cur => ({ ...cur, categories: [c, ...cur.categories] }));
  const updateCategoryInState = (c) => updateStateArray('categories', c.categoryid || c.categoryId || c.id, c);
  const removeCategoryFromState = (id) => filterStateArray('categories', id);

  // Subcategories State
  const addSubcategoryToState = (sc) => setDashboardData(cur => ({ ...cur, subcategories: [sc, ...cur.subcategories] }));
  const updateSubcategoryInState = (sc) => updateStateArray('subcategories', sc.subcatid || sc.subcatId || sc.id, sc);
  const removeSubcategoryFromState = (id) => filterStateArray('subcategories', id);

  // Operations State
  // const updateArtistInState = (a) => updateStateArray('artists', a.id || a.artist_id || a.user_id, a);
  const updateBookingInState = (id, updates) => updateStateArray('bookings', id, updates);

  const fetchProfileAndDashboard = async () => {
    if (!getAuthToken()) {
      handleLogout();
      return;
    }

    setIsDashboardLoading(true);
    setDashboardError("");

    try {
      const res = await vendorArtistApi.profile();
      if (res?.success && res.data) {
        const profile = res.data;
        const status = profile.status;
        const profileExists = !!profile.vendor_artist_id;

        if (profileExists && status === "approved") {
          // Fetch complete dashboard data if approved
          const [cat, subcat, serv, book, users, art, stats] = await Promise.all([
            vendorArtistApi.categories().catch(() => ({ data: [] })),
            vendorArtistApi.subcategories().catch(() => ({ data: [] })),
            vendorArtistApi.services().catch(() => ({ data: [] })),
            vendorArtistApi.bookings().catch(() => ({ data: [] })),
            vendorArtistApi.clients().catch(() => ({ data: [] })),
            vendorArtistApi.artists().catch(() => ({ data: [] })),
            vendorArtistApi.stats().catch(() => ({ data: null })),
          ]);

          setDashboardData({
            profile,
            categories: cat?.data || [],
            subcategories: subcat?.subcategories || subcat?.data || [],
            services: serv?.data || [],
            bookings: book?.data || [],
            users: users?.data || [],
            artists: art?.data || [],
            stats: stats?.data || null,
          });
        } else {
          setDashboardData(cur => ({ ...cur, profile }));
        }
        setProfileLoaded(true);
      } else {
        setDashboardError("Failed to fetch profile details.");
      }
    } catch (err) {
      console.error("Profile load error:", err);
      const errMsg = err.message || "";
      const isAuthError = errMsg.toLowerCase().includes("token") ||
        errMsg.toLowerCase().includes("unauthorized") ||
        errMsg.toLowerCase().includes("auth");

      if (isAuthError) {
        console.warn("[Auth Sync] Detected authorization error, purging session and redirecting to login:", errMsg);
        handleLogout();
      } else {
        setDashboardError(err.message || "Failed to load dashboard data.");
      }
    } finally {
      setIsDashboardLoading(false);
    }
  };

  useEffect(() => {
    fetchProfileAndDashboard();
  }, [navigate]);

  const renderContent = () => {
    switch (activeTab) {
      case "overview": return <DashboardOverview setActiveTab={handleTabChange} dashboardData={dashboardData} />;
      case "profile": return <ProfilePanel profile={dashboardData.profile} onProfileSaved={saveProfileToState} />;
      case "services": return <ServicesPanel dashboardServices={dashboardData.services} dashboardCategories={dashboardData.categories} dashboardSubcategories={dashboardData.subcategories} onServiceCreated={addServiceToState} onServiceUpdated={updateServiceInState} onServiceDeleted={removeServiceFromState} onCategoryCreated={addCategoryToState} onCategoryUpdated={updateCategoryInState} onCategoryDeleted={removeCategoryFromState} onSubcategoryCreated={addSubcategoryToState} onSubcategoryUpdated={updateSubcategoryInState} onSubcategoryDeleted={removeSubcategoryFromState} />;
      case "calendar":
      case "bookings":
        return (
          <CalendarPanel
            dashboardData={dashboardData}
            onBookingChanged={updateBookingInState}
            refreshDashboard={fetchProfileAndDashboard}
            selectedDate={calendarDate}
            setSelectedDate={setCalendarDate}
            view={calendarView}
            setView={setCalendarView}
          />
        );
      case "clients": return <ClientsPanel dashboardUsers={dashboardData.users} bookings={dashboardData.bookings} />;
      // case "artists": return <ArtistsPanel dashboardArtists={dashboardData.artists} onArtistUpdated={updateArtistInState} />;
      case "subscription": return <SubscriptionPanel profile={dashboardData.profile} />;
      case "notifications": return <NotificationsPanel />;
      case "booking": return <BookingLinkPanel profile={dashboardData.profile} services={dashboardData.services} />;
      default: return <DashboardOverview setActiveTab={handleTabChange} dashboardData={dashboardData} />;
    }
  };

  // Helper header for blocked states
  const BlockedHeader = () => (
    <header className="border-b border-slate-200 bg-white/95 shadow-sm backdrop-blur sticky top-0 z-50">
      <div className="mx-auto flex h-16 max-w-[1800px] items-center justify-between px-4 sm:px-6 lg:px-8">
        <div className="flex items-center gap-2">
          <img src={logo} alt="WamGlammz" className="h-9 w-auto object-contain" />
          <img src={logo2} alt="WamGlammz" className="h-6 w-auto object-contain" />
        </div>
        <button
          onClick={handleLogout}
          className="flex h-10 items-center rounded-lg border border-slate-200 bg-white px-4 text-sm font-bold text-slate-700 hover:bg-slate-50 transition shadow-sm"
        >
          Log Out
        </button>
      </div>
    </header>
  );

  // loading and error states
  if (isDashboardLoading || !profileLoaded) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#F5F9FF]">
        <div className="text-center px-4">
          {isDashboardLoading ? (
            <>
              <div className="h-12 w-12 animate-spin rounded-full border-4 border-blue-600 border-t-transparent mx-auto"></div>
              <p className="mt-4 text-sm font-bold text-slate-600">Loading your profile details...</p>
            </>
          ) : (
            <div className="mx-auto max-w-md rounded-2xl border border-red-100 bg-white p-6 shadow-[0_16px_45px_rgba(239,68,68,0.08)] sm:p-8">
              <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-red-50 text-red-500">
                <i className="fa-solid fa-triangle-exclamation text-3xl"></i>
              </div>
              <h2 className="mt-6 text-xl font-extrabold text-[#191E49]">Connection Error</h2>
              <p className="mt-2 text-sm text-slate-500 leading-relaxed">
                {getFriendlyErrorMessage(dashboardError)}
              </p>
              <div className="mt-8 flex gap-4">
                <button
                  onClick={fetchProfileAndDashboard}
                  className="flex-1 h-11 rounded-xl bg-[#0A3FB6] text-sm font-bold text-white shadow-lg shadow-blue-100 hover:shadow-blue-200 transition"
                >
                  Retry
                </button>
                <button
                  onClick={handleLogout}
                  className="flex-1 h-11 rounded-xl border border-slate-200 bg-white text-sm font-bold text-slate-700 hover:bg-slate-50 transition"
                >
                  Log Out
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // check blocking states
  if (profileLoaded && dashboardData.profile) {
    const profile = dashboardData.profile;
    const hasProfile = !!profile.vendor_artist_id;
    const status = profile.status;

    if (!hasProfile) {
      return (
        <div className="min-h-screen bg-[#F5F9FF]">
          <BlockedHeader />
          <div className="py-8">
            <ProfileForm
              initialData={{ name: "", email: "", phone: profile.phone }}
              onSubmitSuccess={fetchProfileAndDashboard}
              onLogout={handleLogout}
            />
          </div>
        </div>
      );
    }

    if (status === 'pending') {
      return (
        <div className="min-h-screen bg-[#F5F9FF]">
          <BlockedHeader />
          <div className="py-8">
            <PendingScreen
              profile={profile}
              onRefresh={fetchProfileAndDashboard}
              onLogout={handleLogout}
            />
          </div>
        </div>
      );
    }

    if (status === 'rejected') {
      return (
        <div className="min-h-screen bg-[#F5F9FF]">
          <BlockedHeader />
          <div className="py-8">
            <RejectedScreen
              profile={profile}
              onSubmitSuccess={fetchProfileAndDashboard}
              onLogout={handleLogout}
            />
          </div>
        </div>
      );
    }

    if (status === 'suspended') {
      return (
        <div className="min-h-screen bg-[#F5F9FF]">
          <BlockedHeader />
          <div className="py-8">
            <SuspendedScreen onLogout={handleLogout} />
          </div>
        </div>
      );
    }
  }

  // normal approved dashboard layout
  const currentPlan = dashboardData.profile?.subscription_plan || "trial";
  const currentStatus = dashboardData.profile?.subscription_status || "active";
  const isSubscribed = currentPlan !== "trial" && currentStatus === "active";

  return (
    <div className="min-h-screen bg-[#F5F9FF] font-sans text-slate-800">
      <WorkspaceHeader
        mobileNavOpen={mobileNavOpen}
        setMobileNavOpen={setMobileNavOpen}
        profile={dashboardData.profile}
        hasNewNotification={hasNewNotification}
        notifications={notifications}
        onClearNotifications={clearNotifications}
        onClearBadge={clearBadge}
        onTabChange={handleTabChange}
        onNotificationClick={(dateString) => {
          setCalendarDate(dateString);
          setCalendarView("daily");
          handleTabChange("calendar");
        }}
      />

      {/* Mobile Sidebar Overlay */}
      {mobileNavOpen && (
        <div className="fixed inset-0 z-50 lg:hidden" role="dialog" aria-modal="true">
          <button type="button" className="absolute inset-0 h-full w-full bg-slate-900/45" onClick={() => setMobileNavOpen(false)} aria-label="Close" />
          <aside className="relative h-full w-[min(22rem,88vw)] overflow-y-auto border-r border-slate-200 bg-[#F5F9FF] shadow-2xl">
            <div className="p-4 pt-16">
              <WorkspaceSidebar activeTab={activeTab} setActiveTab={handleTabChange} onNavigate={() => setMobileNavOpen(false)} profile={dashboardData.profile} />
            </div>
          </aside>
        </div>
      )}

      <div className="mx-auto grid max-w-[1800px] lg:grid-cols-[280px_1fr]">
        <aside className="hidden border-r border-slate-200 bg-white/75 lg:block">
          <div className="sticky top-16 h-[calc(100vh-4rem)] overflow-y-auto p-4">
            <WorkspaceSidebar activeTab={activeTab} setActiveTab={handleTabChange} profile={dashboardData.profile} />
          </div>
        </aside>

        <main className="min-w-0 px-4 py-6 sm:px-6 lg:px-8">
          <div className="mb-6 flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-blue-700">Vendor workspace</p>
              <h1 className="mt-2 text-3xl font-extrabold text-[#191E49] sm:text-4xl">
                {dashboardData.profile?.branding_settings?.businessName || dashboardData.profile?.name || "Your"} dashboard
              </h1>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              {/* <StatusBadge tone="green">Stripe trial active</StatusBadge> */}
              {dashboardData.profile?.booking_slug && (
                isSubscribed ? (
                  <Link to={`/book/${dashboardData.profile.booking_slug}`} target="_blank" className="h-10 rounded-lg bg-gradient-to-r from-[#0A3FB6] to-[#2FB4FF] px-4 py-2 text-sm font-bold text-white shadow-lg shadow-blue-100 hover:shadow-blue-200 transition">
                    Preview booking link
                  </Link>
                ) : (
                  <button
                    type="button"
                    disabled
                    className="h-10 cursor-not-allowed rounded-lg border border-slate-200 bg-slate-100 px-4 py-2 text-sm font-bold text-slate-400 flex items-center gap-2"
                  >
                    <i className="fa-solid fa-lock text-xs" />
                    Preview booking link
                  </button>
                )
              )}
            </div>
          </div>

          {isDashboardLoading && <div className="mb-4 rounded-xl border border-blue-100 bg-white px-4 py-3 text-sm font-bold text-blue-700 shadow-sm">Loading backend dashboard data...</div>}
          {dashboardError && <div className="mb-4 rounded-xl border border-amber-100 bg-amber-50 px-4 py-3 text-sm font-bold text-amber-800">{getFriendlyErrorMessage(dashboardError)}</div>}

          <div>{renderContent()}</div>
        </main>
      </div>
    </div>
  );
}